import { CommunityAnnouncementView, CommunityMeetingRsvp } from './community.js';
import { HttpUrl } from './common/url.js';
import { z } from 'zod';
import { CursorQuery } from './common/pagination.js';
/**
 * 1:1 chat between accepted connections only (FR-CHAT-01, AC-CHAT-01). One
 * thread per pair. Realtime delivery over Socket.IO namespace /chat; REST
 * carries history and the send path (so a client without a socket still
 * works — AC-GL-04 spirit).
 */
export const SendMessageInput = z.object({
    body: z.string().trim().min(1).max(4_000),
    /** Client-generated for optimistic UI + idempotent resend. */
    clientId: z.string().uuid().optional(),
});
export const ChatMessage = z.object({
    id: z.string(),
    threadId: z.string(),
    senderId: z.string(),
    body: z.string(),
    clientId: z.string().optional(),
    createdAt: z.coerce.date(),
    readAt: z.coerce.date().nullable(),
});
export const ThreadView = z.object({
    id: z.string(),
    otherUserId: z.string(),
    otherDisplayName: z.string().nullable(),
    lastMessage: ChatMessage.nullable(),
    unreadCount: z.number().int().nonnegative(),
    updatedAt: z.coerce.date(),
});
export const ThreadListQuery = CursorQuery;
export const MessageListQuery = CursorQuery;
export const GroupChatEmoji = z.enum(['👍', '❤️', '😂', '🎉', '🙏', '🏀']);
export const GroupChatPollInput = z.object({
    options: z
        .array(z.string().trim().min(1).max(100))
        .min(2)
        .max(6)
        .refine((options) => new Set(options.map((option) => option.toLowerCase())).size === options.length, 'Poll options must be distinct'),
});
export const GroupChatMeeting = z.object({
    id: z.string(),
    title: z.string(),
    startsAt: z.coerce.date(),
    timezone: z.string(),
    location: z.string().optional(),
    videoUrl: HttpUrl.optional(),
    photoUrl: HttpUrl.optional(),
    goingCount: z.number().int().nonnegative(),
    maybeCount: z.number().int().nonnegative(),
    myRsvp: CommunityMeetingRsvp.nullable(),
});
export const GroupChatReactionInput = z.object({ emoji: GroupChatEmoji.nullable() });
export const GroupChatVoteInput = z.object({ option: z.number().int().min(0).max(5).nullable() });
export const GroupChatActivityInput = z.object({
    active: z.boolean(),
    typing: z.boolean().default(false),
});
/** REST-only community chat. Membership is checked on every operation. */
export const GroupChatMessage = z.object({
    imageUrl: HttpUrl.optional(),
    kind: z.enum(['message', 'announcement']).optional(),
    senderRole: z.enum(['admin', 'moderator', 'member']).optional(),
    senderPosition: z.string().nullable().optional(),
    seenCount: z.number().int().nonnegative().optional(),
    reactions: z
        .array(z.object({ emoji: GroupChatEmoji, count: z.number().int().positive(), mine: z.boolean() }))
        .optional(),
    poll: z
        .object({
        options: z.array(z.string()),
        counts: z.array(z.number().int().nonnegative()),
        myVote: z.number().int().nullable(),
        totalVotes: z.number().int().nonnegative(),
    })
        .optional(),
    meeting: GroupChatMeeting.optional(),
    announcement: CommunityAnnouncementView.optional(),
    id: z.string(),
    communityId: z.string(),
    senderId: z.string(),
    body: z.string(),
    clientId: z.string(),
    meetingId: z.string().nullable(),
    createdAt: z.coerce.date(),
});
export const SendGroupMessageInput = z
    .object({
    body: z.string().trim().max(4_000),
    clientId: z.string().min(8).max(128),
    imageUrl: HttpUrl.optional(),
    meetingId: z.string().min(1).max(128).optional(),
    poll: GroupChatPollInput.optional(),
})
    .refine((value) => !!value.body || !!value.imageUrl || !!value.meetingId, 'Write a message or attach a photo or event')
    .refine((value) => !value.poll || (!!value.body && !value.imageUrl && !value.meetingId), 'A poll needs a question and cannot include another attachment');
export const GroupChatView = z.object({
    pinnedMessage: GroupChatMessage.nullable(),
    unreadCount: z.number().int().nonnegative(),
    onlineCount: z.number().int().nonnegative().optional(),
    onlineMemberIds: z.array(z.string()).optional(),
    typingMemberIds: z.array(z.string()).optional(),
});
export const PinGroupMessageInput = z.object({ messageId: z.string().min(1).nullable() });
export const ReadGroupChatInput = z.object({ throughMessageId: z.string().min(1) });
//# sourceMappingURL=chat.js.map